import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { useAuth } from "./AuthContext";

const CityContext = createContext();
const KEY = "ponesi_selected_city_id";

// City resolution order (never a hardcoded default):
//   1. authenticated profile city_id  2. manual guest choice in localStorage  3. onboarding (fresh geolocation)
export function CityProvider({ children }) {
  const { user, loading: authLoading, setUser } = useAuth();
  const [cities, setCities] = useState([]);
  const [selectedCityId, setSelected] = useState("");
  const [detectedCityId, setDetectedCityId] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(data)).catch(() => setCities([]));
  }, []);

  // Resolve the active city once cities + auth state are known.
  useEffect(() => {
    if (!cities.length || authLoading) return;
    const valid = (id) => !!id && cities.some((c) => c.id === id);
    const profile = user?.city_id;
    if (valid(profile)) {
      setSelected(profile);
      localStorage.setItem(KEY, profile);
    } else if (!user) {
      const stored = localStorage.getItem(KEY);
      setSelected(valid(stored) ? stored : "");
    } else {
      setSelected("");
    }
    setReady(true);
  }, [cities, user, authLoading]);

  const persistProfile = useCallback(async (id) => {
    if (!user) return;
    try {
      await api.patch("/auth/city", { city_id: id });
      setUser((u) => (u ? { ...u, city_id: id } : u));
    } catch { /* city stays local; profile sync retried on next choice */ }
  }, [user, setUser]);

  // Manual choice always wins and is never auto-overridden later.
  const selectCity = useCallback((id) => {
    setSelected(id);
    localStorage.setItem(KEY, id);
    track("city_selected", { city_id: id, dedupe_key: id });
    persistProfile(id);
  }, [persistProfile]);

  // One-time fresh geolocation -> supported city. Returns a status, never a guess.
  const detectCityByGeolocation = useCallback(async () => {
    if (!navigator.geolocation) return { status: "UNAVAILABLE" };
    let pos;
    try {
      pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject,
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }));
    } catch (e) {
      return { status: e?.code === 1 ? "DENIED" : "ERROR" };
    }
    try {
      const { data } = await api.post("/cities/detect", {
        latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy,
      });
      if (data.status === "OK") {
        setDetectedCityId(data.city_id);
        setSelected(data.city_id);
        localStorage.setItem(KEY, data.city_id);
        persistProfile(data.city_id);
      } else if (data.status === "OUTSIDE") {
        setDetectedCityId("OUTSIDE");
      }
      return data;
    } catch {
      return { status: "ERROR" };
    }
  }, [persistProfile]);

  const selectedCity = cities.find((c) => c.id === selectedCityId) || null;
  const needsCity = ready && cities.length > 0 && !selectedCityId;

  return (
    <CityContext.Provider value={{ cities, selectedCityId, selectedCity, detectedCityId, ready, needsCity,
                                   selectCity, detectCityByGeolocation }}>
      {children}
    </CityContext.Provider>
  );
}

export const useCity = () => useContext(CityContext);
